import { defineStore } from "pinia";
import { ref } from "vue";
import { service } from "/@/cool/service";

export interface TakephotoShop {
	id: number;
	shop_name: string;
	[key: string]: any;
}

interface ShopSwitchListRes {
	list?: TakephotoShop[];
	page?: number;
	size?: number;
	total?: number;
	hasMore?: boolean;
	version?: string;
}

const DEFAULT_PAGE_SIZE = 100;

export const useTakephotoShopStore = defineStore("takephotoShop", function () {
	const list = ref<TakephotoShop[]>([]);
	const loading = ref(false);
	const loaded = ref(false);
	const version = ref("");
	const error = ref<any>(null);

	let pending: Promise<TakephotoShop[]> | null = null;

	async function fetchAll() {
		const all: TakephotoShop[] = [];
		let page = 1;
		let hasMore = true;
		let latestVersion = version.value;

		while (hasMore) {
			const res = (await (service as any).takephoto.shopSwitchList({
				page,
				size: DEFAULT_PAGE_SIZE
			})) as ShopSwitchListRes | TakephotoShop[];

			const data = Array.isArray(res) ? res : res?.list || [];
			all.push(...data);

			if (!Array.isArray(res)) {
				latestVersion = res.version || latestVersion;
				hasMore = Boolean(res.hasMore);
			} else {
				hasMore = data.length >= DEFAULT_PAGE_SIZE;
			}

			if (data.length === 0) {
				hasMore = false;
			}

			page += 1;
		}

		list.value = all;
		version.value = latestVersion;
		loaded.value = true;
		error.value = null;

		return list.value;
	}

	function loadAll(force = false) {
		if (loaded.value && !force) {
			return Promise.resolve(list.value);
		}
		if (pending) {
			return pending;
		}

		loading.value = true;
		pending = fetchAll()
			.catch((err) => {
				error.value = err;
				throw err;
			})
			.finally(() => {
				loading.value = false;
				pending = null;
			});

		return pending;
	}

	function reload() {
		return loadAll(true);
	}

	async function search(keyword = "") {
		const shops = await loadAll();
		const key = keyword.trim().toLowerCase();
		if (!key) {
			return shops;
		}

		return shops.filter((item) => {
			const name = item.shop_name || item.name || "";
			return name.toLowerCase().includes(key);
		});
	}

	return {
		list,
		loading,
		loaded,
		version,
		error,
		loadAll,
		reload,
		search
	};
});
